import { logClientError } from 'src/util/server-log';
import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { Observable, of, from } from "rxjs";
import { switchMap, catchError } from "rxjs/operators";

import { SharedService } from "@services/shared/shared.service";
import { GrpcSubscriptionService } from "@services/grpc-subscription/grpc-subscription.service";

@Injectable({
  providedIn: "root",
})
export class RootGuard implements CanActivate {
  constructor(
    private router: Router,
    private shared: SharedService,
    private cookieService: CookieService,
    private grpcSubscriptionService: GrpcSubscriptionService,
  ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // Support both 'callback' and 'redirectUrl' parameters
    const redirectUrl = next.queryParams["callback"] ?? next.queryParams["redirectUrl"];
    const planId = next.queryParams["plan_id"] ?? next.queryParams["planId"];
    const oauthCode = next.queryParams["code"];

    // If there's an OAuth callback code, redirect to login with the code parameter
    // so LoginComponent can handle the OAuth callback
    if (oauthCode) {
      this.router.navigate(["/entrar"], { queryParams: { code: oauthCode } });
      return of(false);
    }

    if (redirectUrl) {
      this.shared.setRedirectUrl(redirectUrl);
    }
    if (planId) {
      this.shared.setPlanId(planId);
    }

    const sharedPlanId = this.shared.getPlanId();
    if (sharedPlanId) {
      if (this.isAuthenticated()) {
        // Load the plan first to check if it's free
        return this.loadPlanAndDecide(sharedPlanId);
      } else {
        this.router.navigate(["/entrar"]);
      }
    } else {
      if (this.isAuthenticated()) {
        this.router.navigate(["/minha-conta"]);
      } else {
        // Usuário não autenticado sem planId vai para o paywall
        this.router.navigate(["/assine"]);
      }
    }

    return of(false); // Always prevent activating the root route itself
  }

  /**
   * Loads the plan by ID and decides whether to go to checkout or redirect to origin.
   * If the plan is free (price = 0), redirects to origin site.
   * If the plan has a price, redirects to checkout.
   */
  private loadPlanAndDecide(planId: string): Observable<boolean> {
    return from(this.grpcSubscriptionService.getPlanById(planId)).pipe(
      switchMap((response) => {
        const plan = response.toObject().plan;
        this.shared.currentPlan = plan;
        // Check if the plan is free
        if (plan?.price?.amount === 0) {
          this.shared.redirectToOriginAndClearPlan();
          return of(false);
        }
        this.router.navigate(["/checkout/dados-pessoais"]);
        return of(false);
      }),
      catchError((error) => {
        logClientError("RootGuard: Error loading plan:", error);
        // On error, fallback to checkout (existing behavior)
        this.router.navigate(["/checkout/dados-pessoais"]);
        return of(false);
      }),
    );
  }

  private isAuthenticated(): boolean {
    // First, check the shared service
    if (this.shared.accessToken || this.shared.refreshToken) {
      return true;
    }
    // Fallback to checking cookies
    const accessToken = this.cookieService.get("accessToken");
    const refreshToken = this.cookieService.get("refreshToken");
    return !!accessToken || !!refreshToken;
  }
}
